import { useState, useMemo } from 'react';
import { Search, LayoutGrid, List, Filter, X, Columns3 } from 'lucide-react';
import { useLeads, useLeadStats, useClaimLead, useReleaseLead } from '../hooks/useLeads';
import { useAuth, useDevAuth } from '../hooks/useAuth';
import { useRole } from '../contexts/RoleContext';
import { RequirePermission } from '../components/auth/RoleGuard';
import { LeadCard } from '../components/leads/LeadCard';
import { LeadTable } from '../components/leads/LeadTable';
import { LeadModal } from '../components/leads/LeadModal';
import { BoroughColumn } from '../components/leads/BoroughColumn';
import { CuisineFilter } from '../components/filters/CuisineFilter';
import { RegionFilter } from '../components/filters/RegionFilter';
import { NewOnlyToggle } from '../components/filters/NewOnlyToggle';
import { ScoreFilter } from '../components/filters/ScoreFilter';
import { SLAFilter } from '../components/filters/SLAFilter';
import { Button } from '../components/common/Button';
import type { Lead, LeadFilters } from '../api/client';

const useAppAuth = import.meta.env.DEV ? useDevAuth : useAuth;

type ViewMode = 'grid' | 'table' | 'kanban';

const NYC_BOROUGHS = ['Manhattan', 'Brooklyn', 'Queens', 'Bronx', 'Staten Island'];

function LeadsContent() {
  const { user } = useAppAuth();
  const { can } = useRole();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const claimMutation = useClaimLead();
  const releaseMutation = useReleaseLead();

  const canClaim = can('leads:claim');
  const canViewAssignments = can('leads:view_assignments');

  const handleClaim = (id: number) => {
    if (canClaim) {
      claimMutation.mutate(id);
    }
  };

  const handleRelease = (id: number) => {
    if (canClaim) {
      releaseMutation.mutate(id);
    }
  };

  const [filters, setFilters] = useState<LeadFilters>({
    page: 1,
    limit: 50,
    order: 'desc',
    sort: 'first_seen_at',
  });

  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedCuisines, setSelectedCuisines] = useState<string[]>([]);
  const [newOnly, setNewOnly] = useState(false);
  const [minScore, setMinScore] = useState<number | undefined>(undefined);
  const [maxScore, setMaxScore] = useState<number | undefined>(undefined);
  const [slaStatus, setSlaStatus] = useState<'ok' | 'warning' | 'escalated' | undefined>(undefined);

  const queryFilters = useMemo(() => ({
    ...filters,
    region: selectedRegions.length > 0 ? selectedRegions.join(',') : undefined,
    cuisine: selectedCuisines.length > 0 ? selectedCuisines.join(',') : undefined,
    is_new: newOnly ? true : undefined,
    search: searchQuery || undefined,
    min_score: minScore,
    max_score: maxScore,
    sla_status: slaStatus,
  }), [filters, selectedRegions, selectedCuisines, newOnly, searchQuery, minScore, maxScore, slaStatus]);

  const { data, isLoading, error } = useLeads(queryFilters);
  const { data: stats } = useLeadStats();

  const handleSort = (field: string) => {
    setFilters((prev) => ({
      ...prev,
      sort: field,
      order: prev.sort === field && prev.order === 'desc' ? 'asc' : 'desc',
    }));
  };

  const clearFilters = () => {
    setSelectedRegions([]);
    setSelectedCuisines([]);
    setNewOnly(false);
    setSearchQuery('');
    setMinScore(undefined);
    setMaxScore(undefined);
    setSlaStatus(undefined);
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters =
    selectedRegions.length > 0 ||
    selectedCuisines.length > 0 ||
    newOnly ||
    searchQuery ||
    minScore !== undefined ||
    maxScore !== undefined ||
    slaStatus !== undefined;

  const activeFilterCount =
    selectedRegions.length +
    selectedCuisines.length +
    (newOnly ? 1 : 0) +
    (searchQuery ? 1 : 0) +
    (minScore !== undefined || maxScore !== undefined ? 1 : 0) +
    (slaStatus !== undefined ? 1 : 0);

  const leadsByBorough = useMemo(() => {
    if (!data?.leads) return {};
    return NYC_BOROUGHS.reduce((acc, borough) => {
      acc[borough] = data.leads.filter((l) => l.region === borough);
      return acc;
    }, {} as Record<string, Lead[]>);
  }, [data?.leads]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif font-semibold text-font-primary">All Leads</h1>
          <p className="text-font-secondary mt-1">
            Browse and manage all leads across regions
          </p>
        </div>
      </div>

      {/* Active Filters Banner */}
      {hasActiveFilters && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-yellow-600" />
            <span className="text-sm text-yellow-800">
              <strong>{activeFilterCount}</strong> filter{activeFilterCount !== 1 ? 's' : ''} active
              {selectedRegions.length > 0 && (
                <span className="ml-2">
                  Regions: {selectedRegions.join(', ')}
                </span>
              )}
              {selectedCuisines.length > 0 && (
                <span className="ml-2">
                  Cuisines: {selectedCuisines.join(', ')}
                </span>
              )}
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={clearFilters} className="bg-white">
            <X className="w-4 h-4 mr-1" />
            Clear All Filters
          </Button>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-font-secondary" />
          <input
            type="text"
            placeholder="Search leads by name, address..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-font-secondary hover:text-font-primary"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* View mode & filter toggle */}
        <div className="flex items-center space-x-3">
          <NewOnlyToggle
            enabled={newOnly}
            onChange={setNewOnly}
            newCount={stats?.new_count}
          />

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg border transition-colors relative ${
              showFilters
                ? 'bg-brand-primary text-white border-brand-primary'
                : hasActiveFilters
                ? 'bg-yellow-100 text-yellow-700 border-yellow-300'
                : 'bg-white text-font-secondary border-card-border hover:border-brand-primary'
            }`}
          >
            <Filter className="w-5 h-5" />
            {hasActiveFilters && !showFilters && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 text-white text-xs rounded-full flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>

          <div className="flex items-center bg-white border border-card-border rounded-lg">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-l-lg transition-colors ${
                viewMode === 'grid'
                  ? 'bg-brand-primary text-white'
                  : 'text-font-secondary hover:text-brand-primary'
              }`}
              title="Grid view"
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 transition-colors ${
                viewMode === 'table'
                  ? 'bg-brand-primary text-white'
                  : 'text-font-secondary hover:text-brand-primary'
              }`}
              title="Table view"
            >
              <List className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`p-2 rounded-r-lg transition-colors ${
                viewMode === 'kanban'
                  ? 'bg-brand-primary text-white'
                  : 'text-font-secondary hover:text-brand-primary'
              }`}
              title="Kanban view"
            >
              <Columns3 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="card p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-font-primary">Filters</h3>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Clear all
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <CuisineFilter
              selected={selectedCuisines}
              onChange={setSelectedCuisines}
            />
            <RegionFilter
              selected={selectedRegions}
              onChange={setSelectedRegions}
            />
            <ScoreFilter
              minScore={minScore}
              maxScore={maxScore}
              onChange={(min, max) => {
                setMinScore(min);
                setMaxScore(max);
              }}
            />
            <SLAFilter
              selected={slaStatus}
              onChange={setSlaStatus}
            />
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-brand-primary border-r-transparent" />
          <p className="mt-4 text-font-secondary">Loading leads...</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="card p-6 text-center">
          <p className="text-red-600">Failed to load leads. Please try again.</p>
        </div>
      )}

      {/* Leads display */}
      {!isLoading && !error && data && data.leads && (
        <>
          {/* Results count */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-font-secondary">
              Showing {data.leads.length} of {data.total || 0} leads
            </p>
            {data.totalPages > 1 && (
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page === 1}
                  onClick={() => setFilters((f) => ({ ...f, page: (f.page || 1) - 1 }))}
                >
                  Previous
                </Button>
                <span className="text-sm text-font-secondary">
                  Page {filters.page} of {data.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page === data.totalPages}
                  onClick={() => setFilters((f) => ({ ...f, page: (f.page || 1) + 1 }))}
                >
                  Next
                </Button>
              </div>
            )}
          </div>

          {/* Grid view */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {data.leads.map((lead) => (
                <LeadCard
                  key={lead.id}
                  lead={lead}
                  currentUserEmail={user?.email}
                  onClick={() => setSelectedLead(lead)}
                  onClaim={canClaim ? handleClaim : undefined}
                  onRelease={canClaim ? handleRelease : undefined}
                  isClaimLoading={claimMutation.isPending || releaseMutation.isPending}
                  showAssignments={canViewAssignments}
                />
              ))}
            </div>
          )}

          {/* Table view */}
          {viewMode === 'table' && (
            <div className="card overflow-hidden">
              <LeadTable
                leads={data.leads}
                sortField={filters.sort}
                sortOrder={filters.order}
                onSort={handleSort}
                onLeadClick={setSelectedLead}
              />
            </div>
          )}

          {/* Kanban view */}
          {viewMode === 'kanban' && (
            <div className="flex overflow-x-auto space-x-4 pb-4">
              {NYC_BOROUGHS.map((borough) => (
                <BoroughColumn
                  key={borough}
                  borough={borough}
                  leads={leadsByBorough[borough] || []}
                  onLeadClick={setSelectedLead}
                />
              ))}
            </div>
          )}

          {/* No results */}
          {data.leads.length === 0 && (
            <div className="card p-12 text-center">
              <p className="text-font-secondary">No leads found matching your filters.</p>
              {hasActiveFilters && (
                <Button variant="outline" className="mt-4" onClick={clearFilters}>
                  Clear filters
                </Button>
              )}
            </div>
          )}
        </>
      )}

      {/* Lead detail modal */}
      <LeadModal
        lead={selectedLead}
        isOpen={!!selectedLead}
        onClose={() => setSelectedLead(null)}
      />
    </div>
  );
}

export function Leads() {
  return (
    <RequirePermission permission="leads:view">
      <LeadsContent />
    </RequirePermission>
  );
}
